import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CloseOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { authService } from '../services/auth';
import { Product, ProductVariant } from '../types/pz';
import { getApiErrorMessage } from '../utils/apiError';
import {
  createProduct,
  deleteVariant,
  fetchProduct as fetchProductApi,
  fetchProductCategories,
  updateProduct
} from '../modules/products/api';
import {
  canDeleteProductCatalogItem,
  canManageProductCatalog,
} from '../modules/products/productCatalogAccessPolicy';
import {
  ProductCategory,
  categoryErrorMessage,
  createProductCategoryTag,
  fetchProductCategoryTags
} from '../modules/products/productCategoriesApi';
import {
  PACKAGING_BOUNDS,
  POLISH_DECIMAL_INPUT_NUMBER_PROPS,
  packagingPositiveDecimalRule,
  packagingStackLayersRule,
} from '../utils/transportCostAllocation';
import ProductVariantModal from '../components/inventory/ProductVariantModal';
import {
  getProductVariantAttributeEntries,
  getProductVariantName,
} from '../utils/productSelectorLabel';
import './ProductDetail.css';

const { Option } = Select;
const { Text, Title } = Typography;
const { TextArea } = Input;

const currencyOptions = ['PLN', 'USD', 'EUR'];

const formatProductDate = (value: string | undefined, locale: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['products', 'common', 'transport']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [form] = Form.useForm();
  const saveIntentRef = useRef<'stay' | 'back'>('stay');

  const isNewProduct = id === 'new';
  const queryParams = new URLSearchParams(location.search);
  const isEditMode = queryParams.get('mode') === 'edit' || isNewProduct;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryTags, setCategoryTags] = useState<ProductCategory[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [variantModalOpen, setVariantModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(null);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageProductCatalog(currentUser);
  const canDeleteVariant = canDeleteProductCatalogItem(currentUser);
  const canEditPurchasePrices = authService.canEditSupplierPurchasePrices();
  const canEditSalePrices = authService.canEditCustomerSalePrices();

  const isActive = product?.isActive !== false;
  const headerTitle = isNewProduct ? t('products:actions.create') : product?.name;
  const productDateLocale = i18n.language?.startsWith('en') ? 'en-GB' : 'pl-PL';
  const fetchProduct = useCallback(async () => {
    if (!id || isNewProduct) return;

    setLoading(true);
    try {
      const productData = await fetchProductApi(id);
      setProduct(productData);

      form.setFieldsValue({
        code: productData.code,
        name: productData.name,
        englishName: productData.englishName,
        ean: productData.ean,
        description: productData.description,
        category: productData.category,
        productCategoryId: productData.productCategoryId ?? undefined,
        cnCode: productData.cnCode,
        countryOfOrigin: productData.countryOfOrigin,
        netWeight: productData.netWeight,
        grossWeight: productData.grossWeight,
        width: productData.width,
        height: productData.height,
        length: productData.length,
        thickness: productData.thickness,
        piecesPerPackage: productData.piecesPerPackage ?? undefined,
        packageLength: productData.packageLength ?? undefined,
        packageWidth: productData.packageWidth ?? undefined,
        packageStackLayers: productData.packageStackLayers ?? undefined,
        ...(canEditPurchasePrices ? {
          defaultPurchasePrice: productData.defaultPurchasePrice,
          defaultPurchaseCurrency: productData.defaultPurchaseCurrency || 'USD',
        } : {}),
        ...(canEditSalePrices ? {
          defaultSalePrice: productData.defaultSalePrice,
          defaultSaleCurrency: productData.defaultSaleCurrency || 'PLN',
        } : {}),
        status: productData.isActive === false ? 'INACTIVE' : 'ACTIVE',
      });
    } catch (error: any) {
      feedback.error(getApiErrorMessage(error, t, 'products:error.fetchDetailFailed'));
      navigate('/app/products');
    } finally {
      setLoading(false);
    }
  }, [id, isNewProduct, form, navigate, t, feedback, canEditPurchasePrices, canEditSalePrices]);

  const fetchCategories = useCallback(async () => {
    try {
      const categoryData = await fetchProductCategories();
      setCategories(categoryData);
    } catch (error: any) {
      console.error('Error fetching classifications:', error);
    }
  }, []);

  const fetchCategoryTags = useCallback(async () => {
    try {
      setCategoryTags(await fetchProductCategoryTags());
    } catch (error: any) {
      console.error('Error fetching product categories:', error);
    }
  }, []);

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setCreatingCategory(true);
    try {
      const created = await createProductCategoryTag({ name });
      setCategoryTags(await fetchProductCategoryTags());
      form.setFieldsValue({ productCategoryId: created.id });
      setNewCategoryName('');
      feedback.success(t('products:productCategory.createdSuccess', { name }));
    } catch (error: any) {
      feedback.error(categoryErrorMessage(error, t, t('products:productCategory.saveFailed', { name })));
    } finally {
      setCreatingCategory(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCategoryTags();
    if (isNewProduct) {
      // Reset any stale values left over from a previously viewed/edited
      // product before applying the new-product defaults, so switching from
      // an existing product's detail page into "new" mode never bleeds in
      // the old record's data.
      form.resetFields();
      setProduct(null);
      form.setFieldsValue({
        category: 'FINISHED_GOOD',
        status: 'ACTIVE',
      });
    } else {
      fetchProduct();
    }
  }, [isNewProduct, fetchProduct, fetchCategories, fetchCategoryTags, form]);

  const handleSave = async (values: any) => {
    setSaving(true);
    try {
      if (isNewProduct) {
        const createdProduct = await createProduct(values);
        feedback.success(t('products:success.created', { name: values.name }));
        navigate(saveIntentRef.current === 'back' ? '/app/products' : `/app/products/${createdProduct.id}?mode=edit`);
      } else {
        await updateProduct(id!, values);
        feedback.success(t('products:success.updated', { name: values.name }));
        if (saveIntentRef.current === 'back') {
          navigate('/app/products');
        } else {
          navigate(`/app/products/${id}`);
          fetchProduct();
        }
      }
    } catch (error: any) {
      // Polish-first: a stale-category code maps to a translated message; all other
      // product-save errors fall back to getApiErrorMessage's existing handling.
      feedback.error(categoryErrorMessage(
        error,
        t,
        getApiErrorMessage(error, t, 'products:error.saveFailed', undefined, { name: values.name })
      ));
    } finally {
      saveIntentRef.current = 'stay';
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isNewProduct) {
      navigate('/app/products');
      return;
    }
    navigate(`/app/products/${id}`);
  };

  const realVariants = (product?.variants || []).filter((variant) => !variant.isDefault);

  const openVariantModal = (variant?: ProductVariant) => {
    setEditingVariant(variant || null);
    setVariantModalOpen(true);
  };

  const handleDeactivateVariant = async (variant: ProductVariant) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('products:confirm.deleteVariant'),
        okText: t('products:variantEditor.deactivate'),
        cancelText: t('common:cancel'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    const variantName = getProductVariantName(variant);
    try {
      await deleteVariant(variant.id);
      feedback.success(t('products:success.variantDeleted', { name: variantName }));
      await fetchProduct();
    } catch (error: any) {
      const code = error?.response?.data?.code;
      feedback.error(
        code === 'PRODUCT_VARIANT_HAS_STOCK'
          ? t('products:variantEditor.stockDeactivateBlocked')
          : t('products:error.deleteVariantFailed', { name: variantName }),
        { sticky: code === 'PRODUCT_VARIANT_HAS_STOCK' }
      );
    }
  };

  if (loading) {
    return (
      <div className="product-detail-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!isNewProduct && !product) {
    return (
      <div className="product-detail-loading">
        <p>{t('common:notFound', 'Product not found')}</p>
        <Button onClick={() => navigate('/app/products')}>{t('common:back')}</Button>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="product-detail-header">
        <div className="product-detail-header-main">
          <Button
            className="product-detail-back"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/app/products')}
          >
            {t('common:back')}
          </Button>
          <div className="product-detail-title-block">
            <Title level={2}>
              {!isNewProduct && product?.code && (
                <>
                  <span className="product-detail-title-code">{product.code}</span>
                  <span className="product-detail-title-separator">•</span>
                </>
              )}
              <span>{headerTitle}</span>
            </Title>
            <Space wrap size={[8, 6]} className="product-detail-meta">
              {!isNewProduct && (
                <Tag className="product-detail-status-tag" color={isActive ? 'success' : 'default'}>
                  {t(`products:status.${isActive ? 'active' : 'inactive'}`)}
                </Tag>
              )}
              {product?.englishName && <Text type="secondary">{product.englishName}</Text>}
            </Space>
          </div>
        </div>
        <div className="product-detail-header-side">
          <Space className="product-detail-header-actions">
            {isEditMode && (
              <>
                <Button onClick={handleCancel} icon={<CloseOutlined />}>
                  {t('common:cancel')}
                </Button>
                <Button
                  htmlType="submit"
                  form="product-detail-form"
                  loading={saving}
                  onClick={() => { saveIntentRef.current = 'stay'; }}
                  icon={<SaveOutlined />}
                >
                  {t('common:save')}
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  form="product-detail-form"
                  loading={saving}
                  onClick={() => { saveIntentRef.current = 'back'; }}
                  icon={<SaveOutlined />}
                >
                  {t('products:actions.saveAndBack')}
                </Button>
              </>
            )}
            {!isNewProduct && !isEditMode && canEdit && (
              <Button onClick={() => navigate(`/app/products/${id}?mode=edit`)} icon={<EditOutlined />}>
                {t('common:edit')}
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Form
        id="product-detail-form"
        form={form}
        layout="vertical"
        onFinish={handleSave}
        onFinishFailed={() => {
          saveIntentRef.current = 'stay';
          feedback.warning(t('products:fillRequired'));
        }}
        scrollToFirstError
      >
        <section className="product-detail-section product-detail-editor-section">
          <div className="product-detail-section-header product-detail-editor-heading">
            <Title level={4}>{t('products:sections.basicInfo')}</Title>
            <Text type="secondary">{t('products:sections.basicInfoHint')}</Text>
          </div>

          <div className="product-detail-form-grid">
            <div className="product-detail-form-panel product-detail-form-panel-main">
              <div className="product-detail-panel-heading">
                <Text strong>{t('products:sections.catalogData')}</Text>
                <Text type="secondary">{t('products:sections.catalogDataHint')}</Text>
              </div>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label={t('products:fields.code')} name="code">
                    <Input
                      disabled={!isNewProduct}
                      placeholder={t('products:placeholders.code')}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t('products:fields.name')}
                    name="name"
                    rules={[{ required: true, message: t('products:validation.nameRequired') }]}
                  >
                    <Input disabled={!isEditMode} placeholder={t('products:placeholders.name')} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.ean')} name="ean">
                    <Input
                      disabled={!isEditMode}
                      placeholder={t('products:placeholders.ean')}
                      maxLength={64}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.englishName')} name="englishName">
                    <Input disabled={!isEditMode} placeholder={t('products:placeholders.englishName')} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t('products:fields.category')}
                    name="productCategoryId"
                    rules={isNewProduct
                      ? [{ required: true, message: t('products:validation.categoryRequired') }]
                      : []}
                    extra={canEdit ? (
                      <Typography.Link
                        href="/app/products?tab=categories"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('products:productCategory.manageLink')} →
                      </Typography.Link>
                    ) : undefined}
                  >
                    <Select
                      disabled={!isEditMode}
                      placeholder={t('products:placeholders.category')}
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      options={categoryTags.map((tag) => ({ value: tag.id, label: tag.name }))}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          {isEditMode && (
                            <>
                              <Divider style={{ margin: '8px 0' }} />
                              <Space.Compact style={{ width: '100%', padding: '0 8px 8px' }}>
                                <Input
                                  placeholder={t('products:productCategory.namePlaceholder')}
                                  value={newCategoryName}
                                  maxLength={120}
                                  onChange={(event) => setNewCategoryName(event.target.value)}
                                  onKeyDown={(event) => event.stopPropagation()}
                                  onPressEnter={(event) => { event.preventDefault(); handleCreateCategory(); }}
                                />
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  loading={creatingCategory}
                                  onClick={handleCreateCategory}
                                >
                                  {t('products:productCategory.add')}
                                </Button>
                              </Space.Compact>
                            </>
                          )}
                        </>
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t('products:fields.classification')}
                    name="category"
                    rules={[{ required: true, message: t('products:validation.classificationRequired') }]}
                  >
                    <Select disabled={!isEditMode} placeholder={t('products:placeholders.classification')} showSearch>
                      {categories.map(cat => (
                        <Option key={cat} value={cat}>
                          {t(`products:categories.${cat}`, { defaultValue: cat })}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.description')} name="description">
                    <TextArea
                      rows={5}
                      disabled={!isEditMode}
                      placeholder={t('products:placeholders.description')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className="product-detail-form-panel">
              <div className="product-detail-panel-heading">
                <Text strong>{t('products:sections.logisticsData')}</Text>
                <Text type="secondary">{t('products:sections.logisticsDataHint')}</Text>
              </div>
              <Form.Item noStyle name="status" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label={t('products:fields.status')}>
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.status !== next.status}>
                      {() => (
                        <Space className="product-detail-status-control">
                          <Switch
                            checked={form.getFieldValue('status') !== 'INACTIVE'}
                            disabled={!isEditMode}
                            onChange={(checked) => form.setFieldsValue({ status: checked ? 'ACTIVE' : 'INACTIVE' })}
                          />
                          <Text>{t(`products:status.${form.getFieldValue('status') === 'INACTIVE' ? 'inactive' : 'active'}`)}</Text>
                        </Space>
                      )}
                    </Form.Item>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.cnCode')} name="cnCode">
                    <Input disabled={!isEditMode} maxLength={8} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.countryOfOrigin')} name="countryOfOrigin">
                    <Input disabled={!isEditMode} maxLength={2} placeholder="PL" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.width')} name="width">
                    <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.1} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.height')} name="height">
                    <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.1} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.length')} name="length">
                    <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.1} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.thickness')} name="thickness">
                    <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.1} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
                  </Form.Item>
                </Col>

                {/* Packaging (logistics) group — defaults for package-based LDM.
                    Package dims default to the product dims (placeholder hint).
                    No min/max clamp on saved values: rules gate save instead. */}
                <Col span={24}>
                  <Divider orientation="left" style={{ marginTop: 0 }}>
                    <Space size={6}>
                      <span>{t('products:fields.packagingGroup')}</span>
                      <Tooltip title={t('products:fields.packagingGroupHint')}>
                        <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                      </Tooltip>
                    </Space>
                  </Divider>
                </Col>
                {/* No InputNumber min/max clamps: per
                    .claude/rules/frontend-pages.md, antd would silently coerce
                    an out-of-range/negative entry on blur and the form would
                    then save the coerced value. Validation is a custom
                    `validator` rule instead, so an invalid entry stays visible
                    with an inline error and save is blocked. */}
                <Col span={24}>
                  <Form.Item
                    label={t('products:fields.piecesPerPackage')}
                    name="piecesPerPackage"
                    tooltip={t('products:fields.piecesPerPackageTooltip')}
                    rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPiecesPerPackage)]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      disabled={!isEditMode}
                      step={1}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t('products:fields.packageLength')}
                    name="packageLength"
                    tooltip={t('products:fields.packageLengthTooltip')}
                    rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      disabled={!isEditMode}
                      step={1}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t('products:fields.packageWidth')}
                    name="packageWidth"
                    tooltip={t('products:fields.packageWidthTooltip')}
                    rules={[packagingPositiveDecimalRule(t, PACKAGING_BOUNDS.maxPackageDimensionMm)]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      disabled={!isEditMode}
                      step={1}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    label={t('products:fields.packageStackLayers')}
                    name="packageStackLayers"
                    tooltip={t('products:fields.packageStackLayersTooltip')}
                    rules={[packagingStackLayersRule(t, PACKAGING_BOUNDS.minStackLayers, PACKAGING_BOUNDS.maxStackLayers)]}
                  >
                    {/* Integer-only — no decimal separator needed. */}
                    <InputNumber style={{ width: '100%' }} disabled={!isEditMode} step={1} precision={0} />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item label={t('products:fields.netWeight')} name="netWeight">
                    <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.001} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label={t('products:fields.grossWeight')} name="grossWeight">
                    <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.001} />
                  </Form.Item>
                </Col>
                {(canEditPurchasePrices || canEditSalePrices) && (
                  <>
                    {canEditPurchasePrices && (
                      <>
                        <Col span={24}>
                          <Form.Item label={t('products:fields.defaultPurchasePrice')} name="defaultPurchasePrice">
                            <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.01} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item label={t('products:fields.defaultPurchaseCurrency')} name="defaultPurchaseCurrency">
                            <Select disabled={!isEditMode}>
                              {currencyOptions.map(currency => <Option key={currency} value={currency}>{currency}</Option>)}
                            </Select>
                          </Form.Item>
                        </Col>
                      </>
                    )}
                    {canEditSalePrices && (
                      <>
                        <Col span={24}>
                          <Form.Item label={t('products:fields.defaultSalePrice')} name="defaultSalePrice">
                            <InputNumber style={{ width: '100%' }} disabled={!isEditMode} min={0} step={0.01} />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item label={t('products:fields.defaultSaleCurrency')} name="defaultSaleCurrency">
                            <Select disabled={!isEditMode}>
                              {currencyOptions.map(currency => <Option key={currency} value={currency}>{currency}</Option>)}
                            </Select>
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </>
                )}
                {!isNewProduct && (
                  <>
                    <Col span={24}>
                      <div className="product-detail-readonly-field">
                        <span>{t('products:fields.createdAt')}</span>
                        <strong><CalendarOutlined /> {formatProductDate(product?.createdAt, productDateLocale)}</strong>
                      </div>
                    </Col>
                    <Col span={24}>
                      <div className="product-detail-readonly-field">
                        <span>{t('products:fields.updatedAt')}</span>
                        <strong><CalendarOutlined /> {formatProductDate(product?.updatedAt, productDateLocale)}</strong>
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </div>
          </div>
        </section>
      </Form>

      {!isNewProduct && product && (
        <section className="product-detail-section">
          <div className="product-detail-section-header product-detail-variants-header">
            <div>
              <Title level={4}>{t('products:sections.variants')}</Title>
              <Text type="secondary">{t('products:sections.variantsHint')}</Text>
            </div>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openVariantModal()}>
                {t('products:actions.addVariant')}
              </Button>
            )}
          </div>

          {realVariants.length === 0 ? (
            <Alert
              type="info"
              showIcon
              message={t('products:noVariants')}
              description={t('products:variantEditor.basicProduct')}
            />
          ) : (
            <Table<ProductVariant>
              className="product-detail-variants-table"
              rowKey="id"
              pagination={false}
              dataSource={realVariants}
              columns={[
                {
                  title: t('products:variantFields.variantName'),
                  key: 'variantName',
                  render: (_, variant) => {
                    const attributes = getProductVariantAttributeEntries(variant);
                    return attributes.length > 0 ? (
                      <Space wrap size={[4, 4]}>
                        {attributes.map(([key, value]) => (
                          <Tag key={key}>{key}: {value}</Tag>
                        ))}
                      </Space>
                    ) : (
                      <Text strong>{getProductVariantName(variant)}</Text>
                    );
                  },
                },
                {
                  title: t('products:fields.ean'),
                  key: 'ean',
                  render: (_, variant) => variant.eanOverride || <Text type="secondary">{product.ean || '—'}</Text>,
                },
                {
                  title: t('products:fields.packagingGroup'),
                  key: 'packaging',
                  render: (_, variant) => (
                    <Text type="secondary">
                      {variant.piecesPerPackage ?? '—'} {t('products:units.PIECE')} / {t('products:units.PACKAGE')}
                    </Text>
                  ),
                },
                {
                  title: t('products:fields.status'),
                  dataIndex: 'isActive',
                  key: 'status',
                  render: (active) => (
                    <Tag color={active !== false ? 'success' : 'default'}>
                      {t(`products:status.${active !== false ? 'active' : 'inactive'}`)}
                    </Tag>
                  ),
                },
                {
                  title: t('common:actions'),
                  key: 'actions',
                  width: 210,
                  render: (_, variant) => (
                    <Space>
                      {canEdit && (
                        <Button size="small" icon={<EditOutlined />} onClick={() => openVariantModal(variant)}>
                          {t('common:edit')}
                        </Button>
                      )}
                      {canDeleteVariant && variant.isActive !== false && (
                        <Button size="small" danger onClick={() => handleDeactivateVariant(variant)}>
                          {t('products:variantEditor.deactivate')}
                        </Button>
                      )}
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </section>
      )}

      {product && (
        <ProductVariantModal
          open={variantModalOpen}
          product={product}
          variant={editingVariant}
          canEditPurchasePrices={canEditPurchasePrices}
          canEditSalePrices={canEditSalePrices}
          onCancel={() => setVariantModalOpen(false)}
          onSaved={async () => {
            setVariantModalOpen(false);
            setEditingVariant(null);
            await fetchProduct();
          }}
        />
      )}
    </div>
  );
};

export default ProductDetail;
